import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Archive, RefreshCw, PlayCircle } from "lucide-react";
import { RetentionLog } from "@/components/RetentionLog";
import { RestorePreviewDialog } from "@/components/backup/RestorePreviewDialog";
import { useBackupHistoryFilter } from "@/components/backup/useBackupHistoryFilter";
import { useBackupActions } from "@/components/backup/useBackupActions";
import { NextRunCard, BackupProgressCard, BackupStats, BackupEmptyState } from "@/components/backup/BackupVaultCards";
import { GlobalConfigCard } from "@/components/backup/GlobalConfigCard";
import { EmpresaSchedulesCard } from "@/components/backup/EmpresaSchedulesCard";
import { BackupHistoryTable } from "@/components/backup/BackupHistoryTable";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, isSuperAdmin } from "@/lib/roles";

// Cofre de Backups — Super Admin. Lista os arquivos de /app/backups/ com status/origem,
// download autenticado, backup manual, horário global/por empresa, retenção e restore.

const DEFAULT_CONFIG = { backup_hour: 3, retention_days: 30, scheduler: null };

export default function BackupVault() {
  const { user } = useAuth();
  // Delete gated pela matriz: SA sempre pode; outros papéis precisam da capability backup.e
  const canDeleteBackup = isSuperAdmin(user) || hasPerm(user, "backup", "e");
  const [files, setFiles] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [targetEmpresa, setTargetEmpresa] = useState("all");
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [nextRun, setNextRun] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null); // { filename, empresa_nome }

  const load = async () => {
    setLoading(true);
    try {
      const [rf, rc, re_] = await Promise.all([
        api.get("/backups"), api.get("/backups/config"), api.get("/empresas"),
      ]);
      setFiles(rf.data || []);
      setEmpresas(re_.data || []);
      setConfig({
        backup_hour: rc.data?.backup_hour ?? DEFAULT_CONFIG.backup_hour,
        retention_days: rc.data?.retention_days ?? DEFAULT_CONFIG.retention_days,
        scheduler: rc.data?.scheduler || null,
      });
      try {
        const rn = await api.get("/backups/next-run");
        setNextRun(rn.data);
      } catch (e) { console.debug("next-run indisponível", e); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar backups");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const hist = useBackupHistoryFilter(files);
  const rows = hist.filtered;
  const actions = useBackupActions({ load, canDeleteBackup, rows });

  const runNow = async () => {
    setRunning(true);
    try {
      const url = targetEmpresa !== "all" ? `/backups/run-now?target_empresa_id=${targetEmpresa}` : "/backups/run-now";
      // Polling paralelo enquanto roda — mostra qual empresa está sendo processada agora
      const pollTimer = setInterval(load, 1800);
      const r = await api.post(url);
      clearInterval(pollTimer);
      toast.success(`Backup criado: ${r.data.success_count ?? 1}/${r.data.total_empresas ?? 1} empresa(s) processada(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
    finally { setRunning(false); }
  };

  const currentlyProcessing = running ? files.find((f) => f.status === "running") : null;

  return (
    <div className="space-y-6" data-testid="page-backup-vault">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="p-3 rounded-xl bg-indigo-600 text-white"><Archive className="w-6 h-6" /></div>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-3xl font-extrabold leading-tight">Cofre de Backups</h1>
          <p className="text-sm text-muted-foreground">
            Backup automático diário às <b data-testid="current-hour">{String(config.backup_hour).padStart(2, "0")}:00 BRT</b>{" "}
            em <code className="text-xs">/app/backups/</code>. Apenas Super Admin autenticado pode baixar.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="btn-refresh-backups">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Atualizar
        </Button>
        <Select value={targetEmpresa} onValueChange={setTargetEmpresa} disabled={running}>
          <SelectTrigger className="w-[220px]" data-testid="select-target-empresa"><SelectValue placeholder="Empresa alvo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={runNow} disabled={running}
          className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 shadow-lg h-11 px-5 font-semibold"
          data-testid="btn-run-backup-now">
          <PlayCircle className={`w-5 h-5 mr-2 ${running ? "animate-pulse" : ""}`} />
          {running ? "Gerando backup…" : "Gerar Backup Manual Agora"}
        </Button>
      </div>

      <NextRunCard nextRun={nextRun} />
      {running && <BackupProgressCard currentlyProcessing={currentlyProcessing} />}

      <GlobalConfigCard
        savedHour={config.backup_hour} savedRetention={config.retention_days} scheduler={config.scheduler}
        onSaved={(h, rt) => setConfig((c) => ({ ...c, backup_hour: h, retention_days: rt }))}
        onReload={load} />
      <EmpresaSchedulesCard empresas={empresas} />

      <BackupStats files={files} />
      {loading && files.length === 0 && <div className="text-center py-16 text-muted-foreground">Carregando…</div>}
      {!loading && files.length === 0 && <BackupEmptyState />}

      <BackupHistoryTable files={files} rows={rows} hist={hist} empresas={empresas} canDeleteBackup={canDeleteBackup}
        actions={actions} onRestore={(f) => setRestorePreview({ filename: f.filename, empresa_nome: f.empresa_nome })} />

      <RetentionLog showEmpresaColumn />

      <RestorePreviewDialog
        open={!!restorePreview}
        filename={restorePreview?.filename}
        empresa={restorePreview?.empresa_nome}
        onClose={() => setRestorePreview(null)}
        onConfirmed={() => { setRestorePreview(null); load(); }}
      />
    </div>
  );
}
